import { applyI18n, t } from '../i18n';
import { createHelpTip } from '../help/helpTips';
import { iconImg } from './icons';

export interface ModalShellOptions {
  titleKey?: string;
  title?: string;
  titleId?: string;
  helpTopic?: string;
  /** Default true. When false, no header close button (blocking confirms). */
  closable?: boolean;
  className?: string;
  role?: 'dialog' | 'alertdialog';
  labelledBy?: string;
}

export interface ModalShell {
  backdrop: HTMLDivElement;
  dialog: HTMLDivElement;
  header: HTMLDivElement | null;
  body: HTMLDivElement;
  actions: HTMLDivElement;
  titleEl: HTMLHeadingElement | null;
  closeButton: HTMLButtonElement | null;
}

export interface ModalCloseButtonOptions {
  id?: string;
  /** Extra classes appended after `modal-dialog__close`. */
  className?: string;
}

/** Compact header close control shared by modal shells and preview dialogs. */
export function createModalCloseButton(options?: ModalCloseButtonOptions): HTMLButtonElement {
  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.className = ['modal-dialog__close', options?.className].filter(Boolean).join(' ');
  closeButton.id = options?.id ?? 'modal-dialog-close';
  closeButton.setAttribute('data-i18n-aria-label', 'common.close');
  closeButton.setAttribute('aria-label', t('common.close'));
  closeButton.append(iconImg('x', 16));
  return closeButton;
}

export function createModalShell(options: ModalShellOptions): ModalShell {
  const backdrop = document.createElement('div');
  backdrop.className = 'modal-backdrop';
  backdrop.setAttribute('role', 'presentation');

  const dialog = document.createElement('div');
  dialog.className = ['modal-dialog', options.className].filter(Boolean).join(' ');
  dialog.setAttribute('role', options.role ?? 'dialog');
  dialog.setAttribute('aria-modal', 'true');

  const hasTitle = Boolean(options.titleKey || options.title);
  const closable = options.closable ?? true;
  const showHeader = hasTitle || Boolean(options.helpTopic) || closable;

  let header: HTMLDivElement | null = null;
  let titleEl: HTMLHeadingElement | null = null;
  let closeButton: HTMLButtonElement | null = null;

  const body = document.createElement('div');
  body.className = 'modal-dialog__body';

  const actions = document.createElement('div');
  actions.className = 'modal-dialog__actions';

  if (showHeader) {
    header = document.createElement('div');
    header.className = 'modal-dialog__header';

    if (hasTitle) {
      titleEl = document.createElement('h2');
      titleEl.id = options.titleId ?? 'modal-dialog-title';
      titleEl.textContent = options.titleKey ? t(options.titleKey) : (options.title ?? '');
      header.append(titleEl);
    } else {
      header.append(document.createElement('span'));
    }

    const headerActions = document.createElement('div');
    headerActions.className = 'modal-dialog__header-actions';

    if (options.helpTopic) {
      headerActions.append(createHelpTip(options.helpTopic));
    }

    if (closable) {
      closeButton = createModalCloseButton();
      headerActions.append(closeButton);
    }

    header.append(headerActions);
  }

  if (options.labelledBy) {
    dialog.setAttribute('aria-labelledby', options.labelledBy);
  } else if (titleEl) {
    dialog.setAttribute('aria-labelledby', titleEl.id);
  }

  if (header) {
    dialog.append(header);
  }
  dialog.append(body, actions);
  backdrop.append(dialog);

  return { backdrop, dialog, header, body, actions, titleEl, closeButton };
}

export interface ModalDismissOptions {
  backdrop: HTMLDivElement;
  closeButton?: HTMLButtonElement | null;
  onClose: () => void;
  /** When true, Escape and backdrop clicks are ignored. */
  blocking?: boolean;
}

/** Wire Escape, backdrop click, and optional header close. Returns cleanup. */
export function bindModalDismiss(options: ModalDismissOptions): () => void {
  const onKeyDown = (event: KeyboardEvent) => {
    if (options.blocking) {
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      options.onClose();
    }
  };

  const onBackdropClick = (event: MouseEvent) => {
    if (options.blocking) {
      return;
    }
    if (event.target === options.backdrop) {
      options.onClose();
    }
  };

  if (!options.blocking) {
    window.addEventListener('keydown', onKeyDown);
    options.backdrop.addEventListener('click', onBackdropClick);
  }

  options.closeButton?.addEventListener('click', options.onClose);

  return () => {
    if (!options.blocking) {
      window.removeEventListener('keydown', onKeyDown);
      options.backdrop.removeEventListener('click', onBackdropClick);
    }
  };
}

export function refreshModalTitle(
  shell: ModalShell,
  options: Pick<ModalShellOptions, 'titleKey' | 'title'>,
): void {
  if (!shell.titleEl) {
    return;
  }
  shell.titleEl.textContent = options.titleKey ? t(options.titleKey) : (options.title ?? '');
  applyI18n(shell.dialog);
}
